/**
 * KFO Color Combinations - Grid Layout
 * Handles yarn filter, search, accordion, infinite scroll, and lightbox
 */

class KFOCombinations {
  constructor() {
    this.container = document.getElementById('kfo-combinations');
    if (!this.container) return;

    this.grid = document.getElementById('kfo-combinations-grid');
    this.searchInput = this.container.querySelector('.kfo-search-input');
    this.filterTabs = this.container.querySelectorAll('.kfo-filter-tab');

    this.currentlyOpenCard = null;
    this.currentFilter = 'Merino';
    this.searchTerm = '';

    // For reverse lookup (SSH filter)
    this.reverseIndex = null;
    this.showingReverse = false;

    // Pairing image lookup
    this.pairingLookup = null;
    this.contrastingPairings = [];
    this.showingContrasting = false;

    // Load translations
    this.translations = this.loadTranslations();

    this.init();
  }

  init() {
    this.setupFilterTabs();
    this.setupSearch();
    this.setupAccordions();
    this.setupLightbox();
    this.setupZoomButtons();
    this.setupAddToCart();
    this.loadReverseIndexFromJSON();
    this.loadPairingLookup();
    // Apply initial filter
    this.applyFilters();
  }

  /**
   * Load pairing images via Storefront API
   */
  loadPairingLookup() {
    this.pairingLookup = {};
    this.pairingLookupReady = false;

    const configScript = document.getElementById('kfo-api-config');
    if (!configScript) return;

    try {
      const config = JSON.parse(configScript.textContent);
      if (!config.token || !config.shop) return;
      this.fetchPairingsFromAPI(config.shop, config.token);
    } catch (e) {
      // Config parse error - pairing images disabled
    }
  }

  async fetchPairingsFromAPI(shop, token) {
    const buildQuery = (cursor) => `{
      metaobjects(type: "kfo_color_pairing", first: 250${cursor ? `, after: "${cursor}"` : ''}) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          fields {
            key
            value
            reference {
              ... on ProductVariant {
                id
                title
                product {
                  title
                  onlineStoreUrl
                }
              }
              ... on MediaImage {
                image {
                  url
                }
              }
            }
          }
        }
      }
    }`;

    try {
      let hasNextPage = true;
      let cursor = null;

      while (hasNextPage) {
        const response = await fetch(`https://${shop}/api/2024-10/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token
          },
          body: JSON.stringify({ query: buildQuery(cursor) })
        });

        if (!response.ok) break;

        const data = await response.json();
        if (data.errors) break;

        const metaobjects = data.data?.metaobjects;
        const nodes = metaobjects?.nodes || [];

        nodes.forEach(node => {
          const fields = {};
          node.fields.forEach(f => {
            fields[f.key] = f;
          });

          const yarnARef = fields.yarn_a?.reference;
          const yarnBRef = fields.yarn_b?.reference;
          const yarnATitle = yarnARef?.title;
          const yarnBTitle = yarnBRef?.title;
          const imageUrl = fields.pairing_image?.reference?.image?.url;
          const pairingType = fields.pairing_type?.value || 'matching';

          if (yarnATitle && yarnBTitle && imageUrl) {
            const imgData = {
              img: imageUrl + '&width=400',
              imgLarge: imageUrl + '&width=1200',
              yarnA: yarnATitle,
              yarnB: yarnBTitle,
              yarnAUrl: yarnARef?.product?.onlineStoreUrl || '',
              yarnBUrl: yarnBRef?.product?.onlineStoreUrl || '',
              yarnAId: yarnARef?.id ? yarnARef.id.replace('gid://shopify/ProductVariant/', '') : '',
              yarnBId: yarnBRef?.id ? yarnBRef.id.replace('gid://shopify/ProductVariant/', '') : '',
              yarnAType: this.detectYarnType(yarnARef?.product?.title || ''),
              yarnBType: this.detectYarnType(yarnBRef?.product?.title || '')
            };

            const key = `${yarnATitle.toLowerCase().trim()}||${yarnBTitle.toLowerCase().trim()}`;
            this.pairingLookup[key] = imgData;

            if (pairingType === 'contrasting') {
              this.contrastingPairings.push(imgData);
            }
          }
        });

        hasNextPage = metaobjects?.pageInfo?.hasNextPage || false;
        cursor = metaobjects?.pageInfo?.endCursor || null;
      }

      this.pairingLookupReady = true;
    } catch (e) {
      // API fetch failed - pairing images will use defaults
    }
  }

  /**
   * Find pairing image for two colors
   */
  findPairingImage(colorA, colorB) {
    if (!this.pairingLookup) return null;

    const key1 = `${colorA.toLowerCase().trim()}||${colorB.toLowerCase().trim()}`;
    const key2 = `${colorB.toLowerCase().trim()}||${colorA.toLowerCase().trim()}`;

    return this.pairingLookup[key1] || this.pairingLookup[key2] || null;
  }

  /**
   * Apply pairing images to match items in a card
   */
  applyPairingImages(card) {
    const baseColor = card.querySelector('.kfo-grid-card-title')?.textContent?.trim();
    if (!baseColor) return;

    const matchItems = card.querySelectorAll('.kfo-match-item');
    matchItems.forEach(item => {
      const matchColor = item.querySelector('.kfo-match-name')?.textContent?.trim();
      if (!matchColor) return;

      const pairing = this.findPairingImage(baseColor, matchColor);
      if (pairing && pairing.img) {
        const img = item.querySelector('.kfo-match-image');
        const wrapper = item.querySelector('[data-lightbox-trigger]');

        if (img) {
          img.dataset.src = pairing.img;
          img.src = pairing.img;
          img.classList.remove('lazyload');
          img.classList.add('lazyloaded');
        }
        if (wrapper && pairing.imgLarge) {
          wrapper.dataset.pswpSrc = pairing.imgLarge;
        }
      }
    });
  }

  loadTranslations() {
    const defaults = {
      viewProduct: 'Produkt ansehen',
      showCombinations: 'Kombinationen ansehen',
      combinationsHeading: 'Kombinationen:',
      addToCart: 'In den Warenkorb',
      adding: 'Wird hinzugefügt...',
      added: 'Hinzugefügt!',
      error: 'Fehler',
      zoom: 'Vergrößern',
      noResults: 'Keine Farben gefunden.'
    };

    const script = document.getElementById('kfo-translations');
    if (!script) return defaults;

    try {
      return { ...defaults, ...JSON.parse(script.textContent) };
    } catch (e) {
      return defaults;
    }
  }

  /**
   * Yarn Type Filter Tabs
   */
  setupFilterTabs() {
    this.filterTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const filter = tab.dataset.yarnFilter;
        this.setFilter(filter);

        this.filterTabs.forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
      });
    });
  }

  setFilter(filter) {
    this.currentFilter = filter;

    if (filter === 'Contrasting') {
      this.showContrastingView();
    } else if (filter === 'Soft Silk Mohair') {
      this.showReverseView(filter);
    } else {
      this.showForwardView();
    }

    this.applyFilters();
  }

  /**
   * Search Functionality
   */
  setupSearch() {
    if (!this.searchInput) return;

    this.searchInput.addEventListener('input', this.debounce(() => {
      this.searchTerm = this.searchInput.value.toLowerCase().trim();
      this.applyFilters();
    }, 300));
  }

  /**
   * Apply Both Filters
   */
  applyFilters() {
    // Handle contrasting view search
    if (this.showingContrasting) {
      const gallery = this.grid.querySelector('.kfo-contrasting-gallery');
      if (gallery) {
        const items = gallery.querySelectorAll('.kfo-contrasting-item');
        let visibleCount = 0;
        items.forEach(item => {
          const colorA = item.dataset.colorA || '';
          const colorB = item.dataset.colorB || '';
          const matchesSearch = !this.searchTerm || colorA.includes(this.searchTerm) || colorB.includes(this.searchTerm);
          if (matchesSearch) {
            item.classList.remove('hidden');
            visibleCount++;
          } else {
            item.classList.add('hidden');
          }
        });
        this.toggleEmptyState(visibleCount === 0);
      }
      return;
    }

    const cards = this.grid.querySelectorAll('.kfo-grid-card');
    let visibleCount = 0;

    cards.forEach(card => {
      const colorName = card.dataset.colorName || '';
      const yarnType = card.dataset.yarnType || '';

      const matchesFilter = this.currentFilter === 'all' || yarnType === this.currentFilter;
      const matchesSearch = !this.searchTerm || colorName.includes(this.searchTerm);

      if (matchesFilter && matchesSearch) {
        card.classList.remove('hidden');
        visibleCount++;
      } else {
        card.classList.add('hidden');
        if (card.classList.contains('is-expanded')) {
          this.closeAccordion(card);
        }
      }
    });

    this.toggleEmptyState(visibleCount === 0);
  }

  toggleEmptyState(show) {
    let emptyState = this.grid.querySelector('.kfo-empty-state');

    if (show && !emptyState) {
      emptyState = document.createElement('div');
      emptyState.className = 'kfo-empty-state';
      emptyState.innerHTML = `<p>${this.translations.noResults}</p>`;
      this.grid.appendChild(emptyState);
    } else if (!show && emptyState) {
      emptyState.remove();
    }
  }

  /**
   * Accordion Functionality
   */
  setupAccordions() {
    this.grid.addEventListener('click', (e) => {
      // Don't trigger on zoom button
      if (e.target.closest('[data-zoom-trigger]')) return;

      const trigger = e.target.closest('[data-card-trigger]');
      if (!trigger) return;

      const card = trigger.closest('.kfo-grid-card');
      if (!card) return;

      this.toggleAccordion(card);
    });
  }

  toggleAccordion(card) {
    const isExpanded = card.classList.contains('is-expanded');

    if (isExpanded) {
      this.closeAccordion(card);
    } else {
      if (this.currentlyOpenCard && this.currentlyOpenCard !== card) {
        this.closeAccordion(this.currentlyOpenCard);
      }
      this.openAccordion(card);
    }
  }

  openAccordion(card) {
    card.classList.add('is-expanded');
    this.currentlyOpenCard = card;

    // Apply pairing images if lookup is ready, otherwise wait for it
    if (this.pairingLookupReady) {
      this.applyPairingImages(card);
    } else if (!card._pairingRetrySet) {
      card._pairingRetrySet = true;
      const checkInterval = setInterval(() => {
        if (this.pairingLookupReady) {
          clearInterval(checkInterval);
          this.applyPairingImages(card);
        }
      }, 200);
      // Stop checking after 10 seconds
      setTimeout(() => clearInterval(checkInterval), 10000);
    }

    this.lazyLoadImages(card);
  }

  closeAccordion(card) {
    card.classList.remove('is-expanded');
    if (this.currentlyOpenCard === card) {
      this.currentlyOpenCard = null;
    }
  }

  lazyLoadImages(container) {
    const images = container.querySelectorAll('img.lazyload[data-src]');
    images.forEach(img => {
      if (!img.src || img.src === '') {
        img.src = img.dataset.src;
        img.classList.remove('lazyload');
        img.classList.add('lazyloaded');
      }
    });
  }

  /**
   * Zoom Buttons for Overview Images
   */
  setupZoomButtons() {
    document.addEventListener('click', (e) => {
      const zoomBtn = e.target.closest('[data-zoom-trigger]');
      if (!zoomBtn) return;

      e.preventDefault();
      e.stopPropagation();

      const card = zoomBtn.closest('.kfo-grid-card');
      const img = card?.querySelector('.kfo-grid-card-image img');
      if (!img) return;

      const src = img.dataset.zoomSrc || img.src;
      const alt = img.alt || '';

      this.openLightbox([{ src, alt }], 0);
    });
  }

  /**
   * Lightbox
   */
  setupLightbox() {
    this.createLightboxHTML();

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-lightbox-trigger]');
      if (!trigger) return;

      e.preventDefault();

      const container = trigger.closest('.kfo-grid-card') || trigger.closest('.kfo-contrasting-grid');
      if (!container) return;

      const allTriggers = Array.from(container.querySelectorAll('[data-lightbox-trigger]'));

      const items = allTriggers.map(item => ({
        src: item.dataset.pswpSrc,
        alt: item.querySelector('img')?.alt || ''
      }));

      const index = allTriggers.indexOf(trigger);
      this.openLightbox(items, index);
    });
  }

  createLightboxHTML() {
    const lightbox = document.createElement('div');
    lightbox.className = 'kfo-lightbox-overlay';
    lightbox.innerHTML = `
      <button class="kfo-lightbox-close" aria-label="Close">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
      <button class="kfo-lightbox-nav kfo-lightbox-prev" aria-label="Previous">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <button class="kfo-lightbox-nav kfo-lightbox-next" aria-label="Next">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <div class="kfo-lightbox-content">
        <div class="kfo-lightbox-spinner"></div>
        <img class="kfo-lightbox-image" src="" alt="">
      </div>
    `;
    document.body.appendChild(lightbox);

    this.lightbox = lightbox;
    this.lightboxImage = lightbox.querySelector('.kfo-lightbox-image');
    this.lightboxSpinner = lightbox.querySelector('.kfo-lightbox-spinner');
    this.lightboxPrev = lightbox.querySelector('.kfo-lightbox-prev');
    this.lightboxNext = lightbox.querySelector('.kfo-lightbox-next');
    this.lightboxClose = lightbox.querySelector('.kfo-lightbox-close');

    this.lightboxClose.addEventListener('click', () => this.closeLightbox());
    this.lightboxPrev.addEventListener('click', () => this.navigateLightbox(-1));
    this.lightboxNext.addEventListener('click', () => this.navigateLightbox(1));
    this.lightbox.addEventListener('click', (e) => {
      if (e.target === this.lightbox) this.closeLightbox();
    });

    document.addEventListener('keydown', (e) => {
      if (!this.lightbox.classList.contains('active')) return;
      if (e.key === 'Escape') this.closeLightbox();
      if (e.key === 'ArrowLeft') this.navigateLightbox(-1);
      if (e.key === 'ArrowRight') this.navigateLightbox(1);
    });
  }

  openLightbox(items, startIndex = 0) {
    this.lightboxItems = items;
    this.lightboxIndex = startIndex;
    this.showLightboxImage();
    this.lightbox.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeLightbox() {
    this.lightbox.classList.remove('active');
    document.body.style.overflow = '';
  }

  navigateLightbox(direction) {
    this.lightboxIndex += direction;

    if (this.lightboxIndex < 0) {
      this.lightboxIndex = this.lightboxItems.length - 1;
    } else if (this.lightboxIndex >= this.lightboxItems.length) {
      this.lightboxIndex = 0;
    }

    this.showLightboxImage();
  }

  showLightboxImage() {
    const item = this.lightboxItems[this.lightboxIndex];

    // Hide current image and show spinner
    this.lightboxImage.style.opacity = '0';
    this.lightboxSpinner.classList.add('active');

    // Wait for fade-out, then swap src
    setTimeout(() => {
      this.lightboxImage.onload = () => {
        this.lightboxSpinner.classList.remove('active');
        this.lightboxImage.style.opacity = '1';
      };
      this.lightboxImage.onerror = () => {
        this.lightboxSpinner.classList.remove('active');
        this.lightboxImage.style.opacity = '1';
      };
      this.lightboxImage.src = item.src;
      this.lightboxImage.alt = item.alt;

      // If already cached, onload fires synchronously — but just in case:
      if (this.lightboxImage.complete) {
        this.lightboxSpinner.classList.remove('active');
        this.lightboxImage.style.opacity = '1';
      }
    }, 150);

    this.lightboxPrev.disabled = this.lightboxItems.length <= 1;
    this.lightboxNext.disabled = this.lightboxItems.length <= 1;
  }

  /**
   * Reverse Index for SSH Filter
   */
  loadReverseIndexFromJSON() {
    const jsonScript = document.getElementById('kfo-reverse-index');
    if (!jsonScript) {
      this.reverseIndex = { 'Soft Silk Mohair': {} };
      return;
    }

    try {
      this.reverseIndex = JSON.parse(jsonScript.textContent);
    } catch (e) {
      this.reverseIndex = { 'Soft Silk Mohair': {} };
    }
  }

  showReverseView(yarnType) {
    if (!this.reverseIndex || !this.reverseIndex[yarnType]) return;

    const contrastingGallery = this.grid.querySelector('.kfo-contrasting-gallery');
    if (contrastingGallery) contrastingGallery.remove();
    this.showingContrasting = false;

    const originalCards = this.grid.querySelectorAll('.kfo-grid-card:not(.kfo-reverse-card)');
    originalCards.forEach(card => card.classList.add('hidden'));

    const existingReverse = this.grid.querySelectorAll('.kfo-reverse-card');
    existingReverse.forEach(card => card.remove());

    const reverseData = this.reverseIndex[yarnType];
    const sortedColors = Object.keys(reverseData).sort();

    sortedColors.forEach(colorKey => {
      const data = reverseData[colorKey];
      const reverseCard = this.createReverseCard(yarnType, data);
      this.grid.appendChild(reverseCard);
    });

    this.showingReverse = true;
  }

  showContrastingView() {
    // Hide regular and reverse cards
    const allCards = this.grid.querySelectorAll('.kfo-grid-card');
    allCards.forEach(card => card.classList.add('hidden'));

    const reverseCards = this.grid.querySelectorAll('.kfo-reverse-card');
    reverseCards.forEach(card => card.remove());

    // Remove any existing contrasting gallery
    const existing = this.grid.querySelector('.kfo-contrasting-gallery');
    if (existing) existing.remove();

    if (!this.pairingLookupReady) {
      // API not ready yet - retry
      const checkInterval = setInterval(() => {
        if (this.pairingLookupReady) {
          clearInterval(checkInterval);
          if (this.currentFilter === 'Contrasting') {
            this.showContrastingView();
          }
        }
      }, 200);
      setTimeout(() => clearInterval(checkInterval), 10000);
      return;
    }

    if (this.contrastingPairings.length === 0) {
      this.toggleEmptyState(true);
      this.showingContrasting = true;
      return;
    }

    // Create gallery
    const gallery = document.createElement('div');
    gallery.className = 'kfo-contrasting-gallery';

    const galleryGrid = document.createElement('div');
    galleryGrid.className = 'kfo-contrasting-grid';

    this.contrastingPairings.forEach(pairing => {
      const item = document.createElement('div');
      item.className = 'kfo-contrasting-item';
      item.dataset.colorA = pairing.yarnA.toLowerCase();
      item.dataset.colorB = pairing.yarnB.toLowerCase();

      item.innerHTML = `
        <div class="kfo-match-image-wrapper" data-lightbox-trigger data-pswp-src="${pairing.imgLarge}">
          <img src="${pairing.img}" alt="${pairing.yarnA} + ${pairing.yarnB}" class="kfo-match-image" loading="lazy">
          <div class="kfo-image-overlay">
            <svg class="kfo-zoom-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"></circle>
              <path d="m21 21-4.35-4.35" stroke="currentColor" stroke-width="2"></path>
            </svg>
          </div>
        </div>
        <div class="kfo-contrasting-info">
          <div class="kfo-contrasting-variant">
            ${pairing.yarnAType ? `<span class="kfo-match-label">${pairing.yarnAType}</span>` : ''}
            <p class="kfo-match-name">${pairing.yarnA}</p>
            ${pairing.yarnAUrl ? `<a href="${pairing.yarnAUrl}" class="kfo-view-product-link">${this.translations.viewProduct} →</a>` : ''}
            ${pairing.yarnAId ? `<button class="kfo-add-to-cart-btn" data-variant-id="${pairing.yarnAId}">${this.translations.addToCart}</button>` : ''}
          </div>
          <div class="kfo-contrasting-variant">
            ${pairing.yarnBType ? `<span class="kfo-match-label">${pairing.yarnBType}</span>` : ''}
            <p class="kfo-match-name">${pairing.yarnB}</p>
            ${pairing.yarnBUrl ? `<a href="${pairing.yarnBUrl}" class="kfo-view-product-link">${this.translations.viewProduct} →</a>` : ''}
            ${pairing.yarnBId ? `<button class="kfo-add-to-cart-btn" data-variant-id="${pairing.yarnBId}">${this.translations.addToCart}</button>` : ''}
          </div>
        </div>
      `;

      galleryGrid.appendChild(item);
    });

    gallery.appendChild(galleryGrid);
    this.grid.appendChild(gallery);

    this.showingContrasting = true;
    this.showingReverse = false;
  }

  showForwardView() {
    const reverseCards = this.grid.querySelectorAll('.kfo-reverse-card');
    reverseCards.forEach(card => card.remove());

    const contrastingGallery = this.grid.querySelector('.kfo-contrasting-gallery');
    if (contrastingGallery) contrastingGallery.remove();

    const originalCards = this.grid.querySelectorAll('.kfo-grid-card');
    originalCards.forEach(card => card.classList.remove('hidden'));

    this.showingReverse = false;
    this.showingContrasting = false;
  }

  createReverseCard(yarnType, data) {
    const card = document.createElement('div');
    card.className = 'kfo-grid-card kfo-reverse-card';
    card.dataset.colorName = data.colorName.toLowerCase();
    card.dataset.yarnType = yarnType;

    const thumbnailSrc = data.variantImage || '';
    const thumbnailLarge = data.variantImageLarge || thumbnailSrc;
    const productUrl = data.productUrl || '#';
    const totalMatches = data.matches ? data.matches.length : 0;

    const matchesHTML = data.matches.map(match => {
      const imageSrc = match.pairingImage || '';
      const imageSrcLarge = match.pairingImageLarge || imageSrc;
      const colorName = match.colorName || '';
      const viewProductLink = match.productUrl || '#';
      const variantId = match.variantId || '';
      const matchYarnType = match.yarnType || 'Merino';

      return `
        <div class="kfo-match-item">
          <div class="kfo-match-image-wrapper" data-lightbox-trigger data-pswp-src="${imageSrcLarge}">
            <img src="${imageSrc}" alt="${colorName}" class="kfo-match-image" loading="lazy">
            <div class="kfo-image-overlay">
              <svg class="kfo-zoom-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"></circle>
                <path d="m21 21-4.35-4.35" stroke="currentColor" stroke-width="2"></path>
              </svg>
            </div>
          </div>
          <div class="kfo-match-info">
            <p class="kfo-match-label">${matchYarnType}</p>
            <p class="kfo-match-name">${colorName}</p>
            <a href="${viewProductLink}" class="kfo-view-product-link">${this.translations.viewProduct} →</a>
            ${variantId ? `<button class="kfo-add-to-cart-btn" data-variant-id="${variantId}">${this.translations.addToCart}</button>` : ''}
          </div>
        </div>
      `;
    }).join('');

    card.innerHTML = `
      <div class="kfo-grid-card-image" data-card-trigger>
        ${thumbnailSrc ? `
          <img src="${thumbnailSrc}" alt="${data.colorName}" loading="lazy" data-zoom-src="${thumbnailLarge}">
          <button type="button" class="kfo-zoom-btn" data-zoom-trigger aria-label="Vergrößern">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
              <path d="M11 8v6M8 11h6"></path>
            </svg>
          </button>
        ` : ''}
      </div>
      <div class="kfo-grid-card-info">
        <h3 class="kfo-grid-card-title">${data.colorName}</h3>
        <div class="kfo-grid-card-meta">
          <span class="kfo-grid-card-badge">${yarnType}</span>
          ${totalMatches > 0 ? `<span class="kfo-grid-card-count">${totalMatches} Kombinationen</span>` : ''}
        </div>
        <button type="button" class="kfo-grid-card-expand" data-card-trigger>
          <span>Details</span>
          <svg class="kfo-expand-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>
      </div>
      <div class="kfo-grid-card-content" data-card-content>
        <div class="kfo-expanded-header">
          <a href="${productUrl}" class="kfo-product-link">${this.translations.viewProduct} →</a>
        </div>
        <div class="kfo-matches-section">
          <h4 class="kfo-matches-title">${this.translations.combinationsHeading}</h4>
          <div class="kfo-matches-grid">${matchesHTML}</div>
        </div>
      </div>
    `;

    return card;
  }

  /**
   * Add to Cart
   */
  setupAddToCart() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.kfo-add-to-cart-btn');
      if (!btn) return;

      e.preventDefault();

      const variantId = btn.dataset.variantId;
      if (!variantId || btn.classList.contains('adding')) return;

      await this.addToCart(btn, variantId);
    });
  }

  async addToCart(btn, variantId) {
    const originalHTML = btn.innerHTML;
    btn.classList.add('adding');
    btn.innerHTML = this.translations.adding;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (!response.ok) throw new Error('Failed');

      btn.classList.remove('adding');
      btn.classList.add('added');
      btn.innerHTML = this.translations.added;

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      setTimeout(() => {
        btn.classList.remove('added');
        btn.innerHTML = originalHTML;
      }, 2000);

    } catch (error) {
      btn.classList.remove('adding');
      btn.innerHTML = this.translations.error;

      setTimeout(() => {
        btn.innerHTML = originalHTML;
      }, 2000);
    }
  }

  detectYarnType(productTitle) {
    const t = productTitle.toLowerCase();
    if (t.includes('soft silk mohair')) return 'Soft Silk Mohair';
    if (t.includes('compatible cashmere')) return 'Compatible Cashmere';
    if (t.includes('heavy merino')) return 'Heavy Merino';
    if (t.includes('merino')) return 'Merino';
    return '';
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new KFOCombinations());
} else {
  new KFOCombinations();
}
